"use client";

import { useEffect, useRef, useState } from "react";

const steps = [
  {
    number: "01",
    phase: "Logistics",
    title: "Shipment Loading & Sealing",
    body: "Material loaded at bonded warehouse or origin port under supervised conditions. Lot numbers recorded, containers sealed, and pre-shipment weight certificates issued. Bill of Lading prepared with full traceability documentation.",
    duration: "Day 0",
  },
  {
    number: "02",
    phase: "Port Operations",
    title: "Arrival & Discharge",
    body: "Cargo received at destination port and discharged under terminal supervision. Seal integrity verified. Outturn weights recorded and compared against shipping documentation. Any discrepancies documented immediately.",
    duration: "Transit + 1–3 Days",
  },
  {
    number: "03",
    phase: "Sampling",
    title: "Sampling & Moisture Determination",
    body: "Representative samples collected in accordance with agreed international sampling standards. Moisture content determined to establish dry metric tonnage (DMT). Split samples prepared and sealed for both parties.",
    duration: "Days 1–5 Post-Arrival",
  },
  {
    number: "04",
    phase: "Assay",
    title: "Primary Assay",
    body: "Primary laboratory conducts chemical analysis to determine payable antimony content and impurity levels. Results shared with both parties along with certified assay report.",
    duration: "5–10 Business Days",
  },
  {
    number: "05",
    phase: "Dispute Resolution",
    title: "Umpire Assay (If Required)",
    body: "If assay variance exceeds contractual threshold, sealed umpire samples are submitted to a mutually agreed independent laboratory. Umpire results are final and binding for settlement purposes.",
    duration: "Additional 5–10 Days",
  },
  {
    number: "06",
    phase: "Financial Settlement",
    title: "Final Settlement",
    body: "Final payable metal calculated based on agreed pricing formula (e.g., LME reference or fixed pricing). Commercial invoice issued reflecting DMT, assay results, and contractual deductions. Payment executed under agreed terms (LC or TT).",
    duration: "As per Contract Terms",
  },
];

export default function WorkflowStepper() {
    const ref = useRef<HTMLDivElement>(null);
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.08 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy"
            style={{ padding: "120px 0" }}
            aria-labelledby="workflow-heading"
        >
            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                {/* Header */}
                <div
                    className="px-6 lg:px-12 text-center"
                    style={{ maxWidth: "1280px", margin: "0 auto" }}
                >
                    <div
                        className="flex items-center justify-center gap-3 mb-8 reveal"
                    >
                        <span className="type-subheading">Settle & Assay</span>
                    </div>
                    <h1
                        className="reveal reveal-delay-1"
                        style={{
                            fontFamily: "var(--font-serif)",
                            fontSize: "clamp(2.5rem, 5vw, 4rem)",
                            fontWeight: 400,
                            color: "var(--color-white)",
                            lineHeight: 1.05,
                            letterSpacing: "-0.02em",
                            margin: "0 auto",
                        }}
                    >
                        Settlement Workflow
                    </h1>
                    <p
                        className="reveal reveal-delay-2"
                        style={{
                            fontFamily: "var(--font-sans)",
                            fontSize: "1.0625rem",
                            color: "rgba(255,255,255,0.55)",
                            lineHeight: "1.7",
                            marginTop: "20px",
                            margin: "20px auto 0",
                        }}
                    >
                        A structured, auditable process from discharge through assay to 
                        <br />
                        final payment, ensuring transparency and contractual certainty at every stage.
                    </p>
                </div>

                {/* Stepper */}
                <div className="flex flex-col lg:flex-row gap-8 mt-10">
                    {/* Step List */}
                    <div
                        className="flex flex-col gap-0 reveal"
                        style={{ minWidth: "260px" }}
                        role="tablist"
                        aria-label="Workflow steps"
                    >
                        {steps?.map((step, i) => (
                            <button
                                key={step?.number}
                                role="tab"
                                aria-selected={activeStep === i}
                                aria-controls={`step-panel-${i}`}
                                id={`step-tab-${i}`}
                                onClick={() => setActiveStep(i)}
                                className="text-left flex items-center gap-4 transition-all duration-200"
                                style={{
                                    padding: "16px 20px",
                                    background:
                                        activeStep === i
                                            ? "var(--color-navy-mid)"
                                            : "transparent",
                                    borderLeft: `2px solid ${activeStep === i ? "var(--color-red)" : "var(--color-border)"}`,
                                    cursor: "pointer",
                                    border: "none",
                                }}
                            >
                                <span
                                    style={{
                                        fontFamily: "var(--font-serif)",
                                        fontSize: "1.25rem",
                                        color:
                                            activeStep === i
                                                ? "var(--color-red)"
                                                : "var(--color-muted)",
                                        minWidth: "32px",
                                    }}
                                >
                                    {step?.number}
                                </span>
                                <div>
                                    <p
                                        style={{
                                            fontFamily: "var(--font-sans)",
                                            fontSize: "0.6875rem",
                                            fontWeight: 600,
                                            letterSpacing: "0.1em",
                                            textTransform: "uppercase",
                                            color:
                                                activeStep === i
                                                    ? "var(--color-red)"
                                                    : "var(--color-muted)",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        {step?.phase}
                                    </p>
                                    <p
                                        style={{
                                            fontFamily: "var(--font-sans)",
                                            fontSize: "0.9rem",
                                            fontWeight: 500,
                                            color:
                                                activeStep === i
                                                    ? "#FFFFFF" : "var(--color-navy)",
                                        }}
                                    >
                                        {step?.title}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Step Detail Panel */}
                    {steps?.map((step, i) => (
                        <div
                            key={step?.number}
                            id={`step-panel-${i}`}
                            role="tabpanel"
                            aria-labelledby={`step-tab-${i}`}
                            hidden={activeStep !== i}
                            className="flex-1"
                            style={{
                                background: "var(--color-navy-mid)",
                                borderRadius: "4px",
                                padding: "40px 48px",
                            }}
                        >
                            <div className="flex items-start justify-between gap-4 mb-6">
                                <div>
                                    <span
                                        style={{
                                            fontFamily: "var(--font-sans)",
                                            fontSize: "0.6875rem",
                                            fontWeight: 600,
                                            letterSpacing: "0.12em",
                                            textTransform: "uppercase",
                                            color: "var(--color-red)",
                                        }}
                                    >
                                        {step?.phase}
                                    </span>
                                    <h3
                                        style={{
                                            fontFamily: "var(--font-serif)",
                                            fontSize: "1.75rem",
                                            fontWeight: 400,
                                            color: "var(--color-white)",
                                            lineHeight: 1.15,
                                            marginTop: "8px",
                                        }}
                                    >
                                        {step?.title}
                                    </h3>
                                </div>
                                <span
                                    style={{
                                        fontFamily: "var(--font-sans)",
                                        fontSize: "0.75rem",
                                        color: "rgba(255,255,255,0.35)",
                                        background: "rgba(255,255,255,0.06)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        padding: "6px 14px",
                                        borderRadius: "2px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {step?.duration}
                                </span>
                            </div>
                            <p
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "1rem",
                                    color: "rgba(255,255,255,0.62)",
                                    lineHeight: "1.75",
                                }}
                            >
                                {step?.body}
                            </p>

                            {/* Step progress indicator */}
                            <div
                                className="flex items-center gap-2 mt-10"
                                aria-hidden="true"
                            >
                                {steps?.map((_, j) => (
                                    <div
                                        key={j}
                                        style={{
                                            height: "2px",
                                            flex: 1,
                                            background:
                                                j <= activeStep
                                                    ? "var(--color-red)"
                                                    : "rgba(255,255,255,0.1)",
                                            borderRadius: "1px",
                                            transition: "background 250ms",
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-12 text-center reveal reveal-delay-3">
                    <p
                        className="reveal reveal-delay-4"
                        style={{
                            fontFamily: "var(--font-sans)",
                            fontSize: "0.8125rem",
                            color: "rgba(255,255,255,0.3)",
                            lineHeight: "1.6",
                        }}
                    >
                        Fixed-price structures available for contracts ≤12 months.
                        Multi-year contracts may incorporate annual price reset
                        mechanisms indexed to CPI or LME rolling averages.
                    </p>
                </div>

            </div>
        </section>
    );
}